/**
 * Check if a shipment is ENS (Entry Summary Declaration) compliant
 * based on the transport mode and required fields.
 *
 * @param {Object} shipment - The shipment object or form data
 * @returns {boolean} - True if the shipment is ENS compliant
 */
export default function ensCompliant(shipment) {
  // Determine the transport mode
  const transportMode = shipment.default_form || shipment.defaultForm || 'airplane'

  const requiresAirports = transportMode !== 'road'
  const requiresEntryCountry = transportMode !== 'road'

  // Check if shipment has nested conveyance object (API response from View.vue)
  const conveyance = shipment.conveyance || {}
  const hasConveyance = shipment.conveyance !== undefined && shipment.conveyance !== null

  // Helper function to get field value from either top-level or conveyance
  const getField = fieldName => {
    // For View.vue: check conveyance first if it exists
    if (hasConveyance && conveyance[fieldName] !== undefined) {
      return conveyance[fieldName]
    }
    // For ShipmentForm: use top-level field
    return shipment[fieldName]
  }

  // Helper function to check if a field is valid
  // Handles both v-select format {value, label} and API primitive values (string/number)
  const isFieldValid = field => {
    if (field === null || field === undefined) return false
    // Check for v-select format (ShipmentForm)
    if (typeof field === 'object' && field.value !== undefined) {
      return field.value !== null && field.value !== ''
    }
    // Check for API format or direct primitives (View.vue)
    if (typeof field === 'string') return field !== ''
    if (typeof field === 'number') return true
    // For API objects (like carrier with id)
    if (typeof field === 'object' && field.id !== undefined) {
      return field.id !== null && field.id !== ''
    }
    return false
  }

  // Basic fields common to all modes
  const basicCompliant = (shipment.document_number !== '' && shipment.document_number != null)
    && isFieldValid(shipment.carrier)

  // Airplane mode specific validation
  if (transportMode === 'airplane') {
    const origin = shipment.origin || shipment.origin_airport
    const destination = shipment.destination || shipment.destination_airport
    const entryCountry = shipment.entry_country

    return basicCompliant
      && isFieldValid(origin)
      && isFieldValid(destination)
      && isFieldValid(entryCountry)
  }

  // Truck/Road mode specific validation
  if (transportMode === 'road') {
    const truckId = getField('truck_id')
    const transportType = getField('transport_type')
    const nationality = getField('nationality')
    const entryCountry = shipment.entry_country

    const truckDetailsCompliant = (truckId !== '' && truckId != null)
      && isFieldValid(transportType)
      && isFieldValid(nationality)
      && isFieldValid(entryCountry)

    const estimatedArrivalTime = getField('estimated_arrival_time')
    const actualDepartureTime = getField('actual_departure_time')
    const estimatedDepartureTime = getField('estimated_departure_time')

    const timeCompliant = (estimatedArrivalTime !== null && estimatedArrivalTime !== '')
      && ((actualDepartureTime !== null && actualDepartureTime !== '')
          || (estimatedDepartureTime !== null && estimatedDepartureTime !== ''))

    const placeOfAcceptanceLocation = getField('place_of_acceptance_location')
    const placeOfAcceptanceCountry = getField('place_of_acceptance_country')
    const placeOfDeliveryLocation = getField('place_of_delivery_location')
    const placeOfDeliveryCountry = getField('place_of_delivery_country')
    const placeOfLoadingLocation = getField('place_of_loading_location')
    const placeOfLoadingCountry = getField('place_of_loading_country')
    const placeOfUnloadingLocation = getField('place_of_unloading_location')
    const placeOfUnloadingCountry = getField('place_of_unloading_country')

    const locationsCompliant = (placeOfAcceptanceLocation !== '' && placeOfAcceptanceLocation != null)
      && isFieldValid(placeOfAcceptanceCountry)
      && (placeOfDeliveryLocation !== '' && placeOfDeliveryLocation != null)
      && isFieldValid(placeOfDeliveryCountry)
      && (placeOfLoadingLocation !== '' && placeOfLoadingLocation != null)
      && isFieldValid(placeOfLoadingCountry)
      && (placeOfUnloadingLocation !== '' && placeOfUnloadingLocation != null)
      && isFieldValid(placeOfUnloadingCountry)

    const consigneeName = getField('consignee_name')
    const consigneeTypeOfPerson = getField('consignee_type_of_person')
    const consigneeCity = getField('consignee_city')
    const consigneeCountry = getField('consignee_country')
    const consigneeStreet = getField('consignee_street')
    const consigneeStreetNumber = getField('consignee_street_number')

    const consigneeCompliant = (consigneeName !== '' && consigneeName != null)
      && isFieldValid(consigneeTypeOfPerson)
      && (consigneeCity !== '' && consigneeCity != null)
      && isFieldValid(consigneeCountry)
      && (consigneeStreet !== '' && consigneeStreet != null)
      && (consigneeStreetNumber !== '' && consigneeStreetNumber != null)

    const consignorName = getField('consignor_name')
    const consignorTypeOfPerson = getField('consignor_type_of_person')
    const consignorCity = getField('consignor_city')
    const consignorCountry = getField('consignor_country')
    const consignorStreet = getField('consignor_street')
    const consignorStreetNumber = getField('consignor_street_number')

    const consignorCompliant = (consignorName !== '' && consignorName != null)
      && isFieldValid(consignorTypeOfPerson)
      && (consignorCity !== '' && consignorCity != null)
      && isFieldValid(consignorCountry)
      && (consignorStreet !== '' && consignorStreet != null)
      && (consignorStreetNumber !== '' && consignorStreetNumber != null)

    const customsOffice = getField('customs_office_of_first_entry')
    const customsCompliant = customsOffice !== '' && customsOffice != null

    return basicCompliant
      && truckDetailsCompliant
      && timeCompliant
      && locationsCompliant
      && consigneeCompliant
      && consignorCompliant
      && customsCompliant
  }

  // Fallback for other modes
  const origin = shipment.origin || shipment.origin_airport
  const destination = shipment.destination || shipment.destination_airport
  const entryCountry = shipment.entry_country

  return basicCompliant
    && (!requiresAirports || (isFieldValid(origin) && isFieldValid(destination)))
    && (!requiresEntryCountry || isFieldValid(entryCountry))
}
